import { createUseStyles } from 'react-jss';
import { GLOBAL_LETTER_SPACING } from '@/styles/themes/components';
import { withAlpha } from '@/styles/utils';
const HIGHLIGHT_DAY_SIZE = 38;
const HOVER_DAY_SELECTOR = '&:hover:not(:disabled):not([data-selected="true"]):not([data-pressed="true"])';
export const useStyles = createUseStyles((theme) => ({
    root: {
        position: 'relative',
        zIndex: 1,
        '&:focus-within': {
            zIndex: theme.zIndex.dropdown,
        },
        '&[data-popover-layer="true"]': {
            zIndex: theme.zIndex.max,
        },
    },
    inputWrapper: {
        position: 'relative',
        zIndex: 1,
        '& [data-flight-date-input="true"]': {
            height: 56,
            minHeight: 56,
            '-webkit-tap-highlight-color': 'transparent',
            [theme.breakpoints.up('sm')]: {
                height: 56,
                minHeight: 56,
            },
            [theme.breakpoints.up('lg')]: {
                height: 48,
                minHeight: 48,
            },
        },
        '& [data-flight-date-input="true"] input': {
            padding: '28px 54px 7px 13px',
            [theme.breakpoints.up('sm')]: {
                padding: '28px 54px 7px 14px',
            },
            [theme.breakpoints.up('lg')]: {
                padding: '19px 54px 5px 14px',
            },
        },
        '& [data-flight-date-input="true"] *': {
            '-webkit-tap-highlight-color': 'transparent',
        },
    },
    calendarButton: {
        width: 32,
        height: 32,
        marginRight: -5,
        padding: 0,
        border: 0,
        outline: 0,
        borderRadius: 999,
        appearance: 'none',
        background: 'transparent',
        color: theme.colors.sky,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'manipulation',
        userSelect: 'none',
        transition: theme.transition.global,
        '-webkit-tap-highlight-color': 'transparent',
        '-webkit-touch-callout': 'none',
        '&[data-open="true"]': {
            color: theme.colors.white,
        },
        '&:hover:not([data-open="true"])': {
            backgroundColor: theme.mode === 'dark'
                ? theme.colors.textBackground
                : theme.colors.brandTertiary,
        },
        '&:focus, &:focus-visible, &:active': {
            outline: 0,
            boxShadow: 'none',
        },
        '& *': {
            '-webkit-tap-highlight-color': 'transparent',
        },
    },
    postfixIcon: {
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        transform: 'translateX(3px)',
        '& svg': {
            width: 22,
            height: 22,
        },
        '& svg > path': {
            fill: theme.colors.brand,
        },
    },
    postfixConfirm: {
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        transform: 'translateX(3px)',
        width: 32,
        height: 32,
        backgroundColor: theme.colors.brand,
        transition: 'background-color .2s ease',
        borderRadius: 999,
        '& svg': {
            width: 24,
            height: 24,
        },
        '& svg > path': {
            fill: theme.colors.white,
        },
        '$calendarButton:hover &': {
            backgroundColor: theme.colors.brand,
        },
    },
    popover: {
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 'calc(50% - 157px)',
        width: 314,
        maxWidth: 'calc(100vw - 32px)',
        padding: '12px',
        borderRadius: 12,
        background: theme.colors.cloud,
        boxShadow: theme.elevations.dropdown,
        userSelect: 'none',
        zIndex: theme.zIndex.max,
        [theme.breakpoints.down('lg')]: {
            left: 0,
            width: '100%',
            minWidth: 288,
        },
    },
    monthHeaderWrapper: {
        height: 38,
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 0',
        marginBottom: 8,
        userSelect: 'none',
        isolation: 'isolate',
    },
    monthHeader: {
        position: 'relative',
        width: 156,
        height: 30,
        '& select': {
            position: 'absolute',
            inset: 0,
            opacity: 0,
            zIndex: 1,
            cursor: 'pointer',
        },
        '&:hover $monthHeaderTitle': {
            color: theme.colors.brand,
        },
    },
    monthHeaderTitle: {
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        padding: 0,
        border: 0,
        background: 'transparent',
        color: theme.colors.sky,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        transition: theme.transition.global,
        '& span': {
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: GLOBAL_LETTER_SPACING,
            textTransform: 'capitalize',
        },
        '& svg > path': {
            fill: 'currentColor',
        },
    },
    scrollButton: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        margin: '0 -6px',
        padding: 6,
        border: 0,
        background: 'transparent',
        color: theme.colors.brand,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& div': {
            borderRadius: 8,
            flexShrink: 0,
            width: 32,
            height: 32,
            transition: theme.transition.global,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        '& svg > path': {
            fill: theme.colors.brand,
        },
        '&[data-direction="left"] svg': {
            transform: 'rotate(180deg)',
        },
        '&[data-direction="left"]': {
            left: 0,
        },
        '&[data-direction="right"]': {
            right: 0,
        },
        '&:hover': {
            '& div': {
                background: theme.colors.brandTertiary,
            },
        },
    },
    scrollButtonWrapper: {
        position: 'absolute',
        width: 90,
        height: 38,
        display: 'flex',
        padding: '0 10px',
        top: 0,
        left: 0,
        pointerEvents: 'auto',
        background: `linear-gradient(to right, ${withAlpha(theme.colors.cloud, 1)}, ${withAlpha(theme.colors.cloud, 0.7)}, ${withAlpha(theme.colors.cloud, 0)})`,
        '&[data-right="true"]': {
            left: 'auto',
            right: 0,
            justifyContent: 'flex-end',
            background: `linear-gradient(to left, ${withAlpha(theme.colors.cloud, 1)}, ${withAlpha(theme.colors.cloud, 0.7)}, ${withAlpha(theme.colors.cloud, 0)})`,
        },
    },
    staticScrollButtons: {
        position: 'absolute',
        top: 12,
        left: 12,
        right: 12,
        height: 38,
        pointerEvents: 'none',
        zIndex: 2,
        '& $scrollButtonWrapper': {
            pointerEvents: 'auto',
        },
    },
    weekdays: {
        position: 'absolute',
        top: 58,
        left: 12,
        right: 12,
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        textAlign: 'center',
        pointerEvents: 'none',
        zIndex: 1,
    },
    weekdaysSpacer: {
        height: 36,
    },
    monthViewport: {
        overflowX: 'auto',
        width: '100%',
        scrollbarWidth: 'none',
        scrollSnapType: 'x mandatory',
        scrollSnapStop: 'always',
        scrollBehavior: 'smooth',
        overscrollBehaviorBlock: 'contain',
        minHeight: 310,
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    monthes: {
        display: 'flex',
    },
    month: {
        flex: '0 0 100%',
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
        scrollSnapAlign: 'start',
    },
    days: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gridAutoRows: 38,
        textAlign: 'center',
    },
    weekday: {
        width: '100%',
        height: 36,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: 13,
        letterSpacing: GLOBAL_LETTER_SPACING,
        color: theme.colors.textPrimary,
        textTransform: 'uppercase',
    },
    emptyDay: {
        height: 38,
        visibility: 'hidden',
    },
    day: {
        position: 'relative',
        width: '100%',
        height: 38,
        padding: 0,
        border: 0,
        background: 'transparent',
        color: theme.colors.sky,
        fontSize: 14,
        letterSpacing: GLOBAL_LETTER_SPACING,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        isolation: 'isolate',
        '-webkit-tap-highlight-color': 'transparent',
        '&:before': {
            content: '""',
            position: 'absolute',
            width: HIGHLIGHT_DAY_SIZE,
            height: HIGHLIGHT_DAY_SIZE,
            borderRadius: HIGHLIGHT_DAY_SIZE,
            border: `2px solid ${theme.colors.brand}`,
            opacity: 0,
            transition: theme.transition.global,
            zIndex: -1,
        },
        '&:after': {
            content: '""',
            position: 'absolute',
            width: HIGHLIGHT_DAY_SIZE,
            height: HIGHLIGHT_DAY_SIZE,
            borderRadius: HIGHLIGHT_DAY_SIZE,
            backgroundColor: theme.colors.brand,
            opacity: 0,
            transition: theme.transition.global,
            zIndex: -1,
        },
        '& span': {
            position: 'relative',
            zIndex: 1,
        },
        '&[data-disabled="true"]': {
            cursor: 'default',
            opacity: 0.3,
            pointerEvents: 'none',
        },
        '&[data-selected="true"]': {
            color: theme.colors.white,
            '&:after': {
                opacity: 1,
            },
        },
        '&[data-pressed="true"]:not([data-selected="true"])': {
            color: theme.colors.brand,
            '&:before': {
                opacity: 1,
            },
        },
        '&:active:not(:disabled):not([data-selected="true"])': {
            color: theme.colors.brand,
            '&:before': {
                opacity: 1,
            },
        },
        '@media (hover: hover) and (pointer: fine)': {
            [HOVER_DAY_SELECTOR]: {
                color: theme.colors.brand,
                '&:before': {
                    opacity: 1,
                },
            },
        },
    },
    todayMarker: {
        position: 'absolute',
        width: 14,
        height: 2,
        borderRadius: 2,
        backgroundColor: theme.colors.error,
        bottom: 5,
        left: 0,
        right: 0,
        margin: 'auto',
        boxShadow: `0 0 0 2px ${withAlpha(theme.colors.error, 0.08)}`,
    },
}), { name: 'flight-date-picker-input' });
